import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Modal from '@material-ui/core/Modal';
import { Button } from '@material-ui/core';
import { CreditCard, Check, X } from 'tabler-icons-react';
import { asignarTarjeta } from '../../services/cardService';
import '../../styles/tarjetas.css';

const useStyles = makeStyles((theme) => ({
    paper: {
        width: 'clamp(18rem, 34vw, 32.5rem)',
        height: 'clamp(16rem, 27vh, 26.25rem)',
        backgroundColor: theme.palette.background.paper,
        borderRadius: 3,
        boxShadow: theme.shadows[2],
        display: 'flex',
        flexDirection: 'column',
        outline: 'none',
    },
    header: {
        backgroundColor: 'var(--color-primary, #4c2e7f)',
        borderRadius: '3px 3px 0 0',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    headerTitle: {
        fontFamily: 'WorkSans',
        fontWeight: 600,
        fontSize: '1.05rem',
        color: '#ffffff',
        margin: 0,
    },
}));

export default function ModalAsignarTarjeta({ open, usuario, token, celda, onConfirm, onCancel }) {
    const classes = useStyles();
    // step: 'waiting' | 'success' | 'timeout' | 'conflict' | 'error'
    const [step, setStep] = useState('waiting');
    const [errorMsg, setErrorMsg] = useState('');
    const abortControllerRef = useRef(null);

    const nombreCompleto = usuario ? `${usuario.nombre} ${usuario.apellido}` : '';

    const launchRequest = () => {
        const controller = new AbortController();
        abortControllerRef.current = controller;
        setStep('waiting');
        setErrorMsg('');

        asignarTarjeta(celda, usuario?.id, token, controller.signal)
            .then(({ status, message }) => {
                if (status === 200) {
                    onConfirm(usuario.id);
                    setStep('success');
                } else if (status === 408) {
                    setStep('timeout');
                } else if (status === 409) {
                    setErrorMsg(message);
                    setStep('conflict');
                } else {
                    setErrorMsg(message || 'Error inesperado');
                    setStep('error');
                }
            })
            .catch((err) => {
                if (err.name === 'AbortError') {
                    // Usuario canceló — el modal se cierra desde handleCancel
                    return;
                }
                setErrorMsg('Error de conexión');
                setStep('error');
            });
    };

    // Lanzar llamada al abrir el modal
    useEffect(() => {
        if (!open || !usuario) return;
        launchRequest();
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, usuario]);

    const handleCancel = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        onCancel();
    };

    const handleRetry = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        launchRequest();
    };

    const renderStep = () => {
        switch (step) {
            case 'waiting':
                return (
                    <div className="tarjetas-modal-step">
                        <div className="tarjetas-pulse">
                            <CreditCard size={52} />
                        </div>
                        <p className="tarjetas-modal-step-text">
                            Pase el gafete por el lector de la celda {celda}...
                        </p>
                        <div className="tarjetas-modal-actions">
                            <Button variant="outlined" onClick={handleCancel}>Cancelar</Button>
                        </div>
                    </div>
                );
            case 'success':
                return (
                    <div className="tarjetas-modal-step">
                        <div className="tarjetas-icon--success">
                            <Check size={52} />
                        </div>
                        <p className="tarjetas-modal-step-text">Tarjeta asignada exitosamente</p>
                        <div className="tarjetas-modal-actions">
                            <Button variant="contained" color="primary" onClick={handleCancel}>
                                Cerrar
                            </Button>
                        </div>
                    </div>
                );
            case 'timeout':
                return (
                    <div className="tarjetas-modal-step">
                        <div className="tarjetas-icon--error">
                            <X size={52} />
                        </div>
                        <p className="tarjetas-modal-step-text">Tiempo agotado, intente de nuevo</p>
                        <div className="tarjetas-modal-actions">
                            <Button variant="contained" color="primary" onClick={handleRetry}>
                                Reintentar
                            </Button>
                            <Button variant="outlined" onClick={handleCancel}>Cancelar</Button>
                        </div>
                    </div>
                );
            case 'conflict':
                return (
                    <div className="tarjetas-modal-step">
                        <div className="tarjetas-icon--error">
                            <X size={52} />
                        </div>
                        <p className="tarjetas-modal-step-text">{errorMsg}</p>
                        <div className="tarjetas-modal-actions">
                            <Button variant="contained" color="primary" onClick={handleRetry}>
                                Reintentar
                            </Button>
                            <Button variant="outlined" onClick={handleCancel}>Cancelar</Button>
                        </div>
                    </div>
                );
            case 'error':
                return (
                    <div className="tarjetas-modal-step">
                        <div className="tarjetas-icon--error">
                            <X size={52} />
                        </div>
                        <p className="tarjetas-modal-step-text">{errorMsg || 'No se pudo completar la asignación'}</p>
                        <div className="tarjetas-modal-actions">
                            <Button variant="contained" color="primary" onClick={handleRetry}>
                                Reintentar
                            </Button>
                            <Button variant="outlined" onClick={handleCancel}>Cancelar</Button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <Modal open={open} onClose={handleCancel}>
            <div className={`${classes.paper} tarjetas-modal-paper`}>
                <div className={classes.header}>
                    <p className={classes.headerTitle}>Asignar Tarjeta RFID</p>
                    {usuario && (
                        <p className={classes.headerTitle}>{nombreCompleto}</p>
                    )}
                </div>
                <div className="tarjetas-modal-body">
                    {renderStep()}
                </div>
            </div>
        </Modal>
    );
}
